/* eslint-disable prettier/prettier */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import LockIcon from '@material-ui/icons/Lock';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import DOMPurify from 'dompurify';
import {
  BASE_API,
  BUTTON_BACKGROUND_COLOR,
  BUTTON_TEXT_COLOR,
  GENERAL_TEXT_COLOR,
  USER_TOKEN,
} from 'config/consts';
import { DomPurifyContentContainer } from './styles';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
  },
  amount: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: theme.palette.primary.main,
    margin: theme.spacing(2, 0),
  },
  payButton: {
    padding: theme.spacing(1),
    fontSize: '0.95rem',
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
  },
  securityInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  securityIcon: {
    marginRight: theme.spacing(1),
    color: '#4caf50',
  },
}));

function parseActivityConfig(activity) {
  try {
    if (activity?.station_specific_data) {
      const raw = activity.station_specific_data;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    }
    if (activity?.body) {
      const raw = activity.body;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    }
  } catch (error) {
    console.warn('Erro ao parsear configuração do link de pagamento:', error);
  }
  return {};
}

function formatCurrency(value) {
  return `R$ ${(value || 0).toFixed(2).replace('.', ',')}`;
}

function ActivityLinkPagamento({ activities, auth }) {
  const classes = useStyles();
  const [submitting, setSubmitting] = useState(false);
  const activity = activities.detail;
  const parsedBody = useMemo(() => parseActivityConfig(activity), [activity]);
  const linkValue = parseFloat(parsedBody.link_value || 0) || 0;
  const descriptionHtml = parsedBody.description || parsedBody.custom_message || activity?.description || '';
  const paymentMethods = parsedBody.payment_methods || ['pix', 'credit_card', 'boleto'];

  const handlePay = async () => {
    if (!activity?.id) {
      toastr.error('Ops!', 'Não foi possível identificar a atividade.');
      return;
    }
    if (linkValue <= 0) {
      toastr.warning('Atenção', 'Esta atividade ainda não tem um valor de pagamento definido.');
      return;
    }
    if (!auth.user?.email || !auth.user?.phone) {
      toastr.warning('Atenção', 'Informe um e-mail e telefone no seu perfil para continuar.');
      return;
    }

    setSubmitting(true);
    try {
      const partnersSplit = parsedBody.partners_split || [];
      const mainPartner = partnersSplit[0] || null;
      const hasPartner = !!(parsedBody.has_partner || mainPartner);
      let partnerPercentage = 0;
      if (hasPartner && mainPartner?.percentage != null) {
        partnerPercentage = parseFloat(mainPartner.percentage) || 0;
      } else if (hasPartner) {
        partnerPercentage = parseFloat(parsedBody.partner_percentage) || 0;
      }
      partnerPercentage = Math.max(0, Math.min(100, partnerPercentage));
      const stationPercentage = hasPartner
        ? Math.max(0, Math.min(100, 100 - partnerPercentage))
        : 100;

      const payload = {
        customer_name: auth.user?.name || '',
        customer_cpf: auth.user?.cpf || '',
        customer_email: auth.user?.email || '',
        customer_phone: auth.user?.phone || '',
        service_name: activity?.name || parsedBody.name || 'Link de Pagamento',
        link_value: linkValue,
        payment_methods: (paymentMethods || []).map((method) =>
          method === 'credit_card' ? 'cartao' : method
        ),
        allow_installments: !!parsedBody.allow_installments,
        has_partner: hasPartner,
        station_percentage: hasPartner
          ? parseFloat(parsedBody.station_percentage) || stationPercentage
          : 100,
        partner_percentage: partnerPercentage,
        partner_recipient_id: hasPartner
          ? mainPartner?.recipient_id || parsedBody.partner_recipient_id || null
          : null,
        partner_user_id: hasPartner
          ? mainPartner?.user_id || parsedBody.partner_user_id || null
          : null,
        partner_name: hasPartner
          ? mainPartner?.name || parsedBody.partner_name || null
          : null,
        station_id: auth.user?.station_id || auth.user?.station?.id || activity.station_id,
        establishment_id: activity.station?.establishment_id || null,
        activity_id: activity.id,
        recurrence_type: parsedBody.recurrence_type || 'single',
      };

      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.post(`${BASE_API}/payment-links/generate`, payload, {
        headers: { Authorization: token },
      });

      if (response.data?.success && response.data?.payment_link?.checkout_url) {
        window.location.href = response.data.payment_link.checkout_url;
        return;
      }
      toastr.error('Erro', response.data?.message || 'Erro ao gerar link de pagamento.');
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || 'Erro ao processar pagamento.';
      toastr.error('Erro', message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid item container xs={12} justifyContent="center" alignItems="center" className={classes.root}>
      {descriptionHtml ? (
        <Grid item xs={12}>
          <Typography variant="body1" component="div" style={{ color: GENERAL_TEXT_COLOR }}>
            <DomPurifyContentContainer
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(descriptionHtml) }}
            />
          </Typography>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <Typography align="center" className={classes.amount}>
          {formatCurrency(linkValue)}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          fullWidth
          className={classes.payButton}
          onClick={handlePay}
          disabled={submitting || linkValue <= 0}
          style={{
            backgroundColor: BUTTON_BACKGROUND_COLOR,
            color: BUTTON_TEXT_COLOR,
          }}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : `Pagar agora - ${formatCurrency(linkValue)}`}
        </Button>
        <Box className={classes.securityInfo}>
          <LockIcon className={classes.securityIcon} fontSize="small" />
          <Typography variant="caption">
            Ambiente criptografado e 100% seguro - SSL Certificado
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

ActivityLinkPagamento.propTypes = {
  activities: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  activities: state.activities,
  auth: state.auth,
});

export default connect(mapStateToProps)(ActivityLinkPagamento);
